// src/app/forgot-password/page.tsx
'use client'

import { FormEvent, useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

/**
 * Forgot-password entry. Asks for the user's email, posts to
 * /api/auth/forgot-password. The endpoint is leak-safe (200 OK
 * regardless), so this page's success message is intentionally
 * generic: it can't say "we sent you a link" with certainty (the
 * email might not match an account at all), it can only say "if
 * we have an account, you'll get a link."
 *
 * That's the same enumeration-safe pattern the /signup page uses.
 */
export default function ForgotPasswordPage() {
  const [pending, setPending] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setPending(true)
    const form = e.currentTarget as HTMLFormElement & {
      email: HTMLInputElement
    }
    const email = form.email.value

    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        cache: 'no-store',
      })
      // The endpoint returns 200 in all "expected" cases (unknown
      // email, OAuth-only account, unverified, happy path). The only
      // non-200s are 429 (rate-limited).
      if (res.status === 429) {
        setError('Too many requests. Please wait a few minutes and try again.')
        return
      }
      setSubmittedEmail(email)
      setSubmitted(true)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setPending(false)
    }
  }

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-md px-4 py-12">
        <Card className="border-border/70 bg-card/60">
          <CardHeader>
            <CardTitle className="text-foreground">Reset your password</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!submitted ? (
              <>
                <p id="forgot-password-help" className="text-sm text-muted-foreground">
                  Enter the email you signed up with. We&apos;ll send you a link to choose a new
                  password.
                </p>
                <form className="space-y-3" onSubmit={onSubmit}>
                  <div>
                    <label
                      htmlFor="forgot-password-email"
                      className="block text-sm text-muted-foreground"
                    >
                      Email
                    </label>
                    <input
                      id="forgot-password-email"
                      type="email"
                      name="email"
                      aria-describedby="forgot-password-help"
                      required
                      autoComplete="email"
                      className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </div>
                  <Button
                    disabled={pending}
                    className="w-full bg-brand text-on-brand"
                    type="submit"
                  >
                    {pending ? 'Sending...' : 'Send reset link'}
                  </Button>
                  {error && (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {error}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    <Link className="underline decoration-slate-500" href="/signin">
                      Back to sign in
                    </Link>
                  </p>
                </form>
              </>
            ) : (
              // Leak-safe success state. We do NOT say "we sent you a
              // link to <email>" — instead, the same message regardless
              // of whether the email matches an account. The user with
              // a real account sees this AND gets an email; an
              // attacker probing for valid emails sees this with no
              // email actually sent.
              <div className="space-y-3">
                <p className="text-sm text-foreground font-medium">Check your inbox.</p>
                <p className="text-sm text-muted-foreground">
                  If we have an account for{' '}
                  <span className="text-foreground">{submittedEmail}</span> with a password,
                  we&apos;ve sent a link to reset it. The link expires in 60 minutes.
                </p>
                <p className="text-xs text-muted-foreground">
                  Don&apos;t see it? Check your spam folder, or{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false)
                      setSubmittedEmail('')
                    }}
                    className="underline decoration-slate-500"
                  >
                    try a different email
                  </button>
                  .
                </p>
                <p className="text-xs text-muted-foreground">
                  <Link className="underline decoration-slate-500" href="/signin">
                    Back to sign in
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
